import FlowSubjectRow from "../features/calculator/components/FlowSubjectRow";
import FlowChoice from "../features/calculator/components/FlowChoice";
import FlowLevelSelect from "../features/calculator/components/FlowLevelSelect";
import useFlowDefaults from "../features/calculator/hooks/useFlowDefaults";
import React, { useState, useEffect } from "react";
import { useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useTZ } from "../contexts/TimezoneContext";
import { useGradeActions } from "../contexts/GradesContext";
import {
  getEntryForSubject,
} from "../utils/timezone";
import {
  getBoundaryResolutionOrder,
} from "../utils/boundaryCatalog";
import useBoundaryCatalog from "../hooks/useBoundaryCatalog";
import { getBoundarySubjectMeta } from "../data/subjectCatalog";
import { getPointsAboveBoundary } from "../features/subjects/utils/subjectScoring";

const TYPE_VARIANTS = [
  ["Language and Literature", [/lang(?:uage)?\s+and\s+literature$/i]],
  ["Literature", [/^(?!.*\band\s+literature\b).*literature(?:\s+self\s+taught)?$/i]],
];

const toCanonicalType = raw => {
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, " ").trim();
  for (const [canonical, patterns] of TYPE_VARIANTS) {
    if (patterns.some(pattern => pattern.test(normalized))) {
      return canonical;
    }
  }
  return null;
};

const parseSubjectKey = key => {
  const match = key.match(/^(SL|HL)\s+(.+?)\s+A:\s+(.+)$/i);
  if (!match) return null;
  const level = match[1].toUpperCase();
  const languageSegment = match[2].replace(/\s+Language$/i, "").trim();
  const rawType = match[3];
  const canonicalType = toCanonicalType(rawType);
  if (!canonicalType) return null;
  const selfTaught = /self\s+taught/i.test(rawType);
  const language = selfTaught ? `${languageSegment} (Self Taught)` : languageSegment;
  return { level, language, type: canonicalType, key };
};

/* ─── smooth red→green color based on 0‑100% ─── */

function GroupOne({
  groupId = "1",
  boundary = "m24",
  headerExtras = null,
  afterTitleExtras = null,
}) {
  const { tz } = useTZ();
  const { updateGrade } = useGradeActions();
  const { dataByBoundary, catalogKeys, isLoading } = useBoundaryCatalog(boundary, {
    includeAllBoundaries: true,
  });

  const storagePath = `groups.${groupId}.groupOne`;
  useFlowDefaults(storagePath, "groupOne", !isLoading);
  const [type, setType] = useCalculatorStoredValue(`${storagePath}.type`, "");
  const [level, setLvl] = useCalculatorStoredValue(`${storagePath}.level`, "");
  const [language, setLang] = useCalculatorStoredValue(`${storagePath}.language`, "");
  const [scores, setSc] = useCalculatorStoredValue(`${storagePath}.scores`, { p1: "", p2: "", essay: "", io: "" });
  const [boundaryNotFound, setBoundaryNotFound] = useState(false);

  useEffect(() => {
    setBoundaryNotFound(false);
  }, [boundary]);

  const TYPES = ["Literature", "Language and Literature"];
  const LEVELS = ["SL", "HL"];
  const isHL = level === "HL";

  const boundaryOrder = getBoundaryResolutionOrder(boundary);
  const parsedSubjects = catalogKeys
    .filter((key) => getBoundarySubjectMeta(key)?.groupId === "1")
    .map(parseSubjectKey)
    .filter(Boolean);

  const getLanguages = (nextType, nextLevel) =>
    catalogKeys.length > 0 && nextType && nextLevel
      ? Array.from(
          new Set(
            parsedSubjects
              .filter(subj => subj.level === nextLevel && subj.type === nextType)
              .map(subj => subj.language)
          )
        ).sort((a, b) => a.localeCompare(b))
      : [];

  const languages = getLanguages(type, level);

  const pickLanguage = (nextType, nextLevel) => {
    const available = getLanguages(nextType, nextLevel);
    if (available.includes(language)) return language;
    return available.includes("English") ? "English" : "";
  };

  const hasCompleteScores =
    type &&
    level &&
    language &&
    scores.p1 !== "" &&
    scores.p2 !== "" &&
    scores.io !== "" &&
    (!isHL || scores.essay !== "");

  let entry = null;
  let resolvedBoundary = null;

  if (hasCompleteScores) {
    for (const candidateBoundary of boundaryOrder) {
      const candidateData = dataByBoundary[candidateBoundary];
      if (!candidateData) continue;

      const candidateKeys = Object.keys(candidateData.timezones || {}).flatMap((zone) =>
        Object.keys(candidateData.timezones?.[zone] || {})
      );
      const candidateSubjects = candidateKeys.map(parseSubjectKey).filter(Boolean);
      const subjectMeta = candidateSubjects.find(
        subj => subj.level === level && subj.type === type && subj.language === language
      );
      if (!subjectMeta) continue;

      const candidateEntry = getEntryForSubject(candidateData.timezones, subjectMeta.key, tz).entry;
      if (candidateEntry) {
        entry = candidateEntry;
        resolvedBoundary = candidateBoundary;
        break;
      }
    }
  }

  const entryAvailable = Boolean(entry);

  useEffect(() => {
    if (isLoading) return;

    if (!hasCompleteScores) {
      if (boundaryNotFound) {
        setBoundaryNotFound(false);
      }
      return;
    }

    if (!entryAvailable) {
      setBoundaryNotFound((prev) => (prev ? prev : true));
    } else if (boundaryNotFound) {
      setBoundaryNotFound(false);
    }
  }, [isLoading, hasCompleteScores, entryAvailable, boundaryNotFound]);

  let grade = null;
  let neededText = null;
  let pointsAboveBoundary = null;

  if (hasCompleteScores && entry) {
    const p1 = Number(scores.p1),
      p2 = Number(scores.p2),
      io = Number(scores.io),
      es = isHL ? Number(scores.essay) : 0;

    const cutoffs = Array.from({ length: 7 }, (_, i) =>
      parseFloat(entry[String(i + 1)])
    );
    const total = isHL
      ? (p1 / 40) * 35 + (p2 / 30) * 25 + (es / 20) * 20 + (io / 40) * 20
      : (p1 / 20) * 35 + (p2 / 30) * 35 + (io / 40) * 30;

    for (let i = 6; i >= 0; i--) {
      if (total >= cutoffs[i]) {
        grade = i + 1;
        if (i < 6) {
          const diff = (cutoffs[i + 1] - total).toFixed(1);
          neededText = `Need ${diff} percentage points for grade ${i + 2}`;
        }
        break;
      }
    }

    if (grade !== null) {
      pointsAboveBoundary = getPointsAboveBoundary(total, entry, grade);
    }
  }

  useEffect(() => {
    if (isLoading) return;

    const subject =
      language && type ? `${language} Language A: ${type}` : null;

    if (grade !== null && subject && level) {
      updateGrade(groupId, grade, subject, level, neededText);
    } else {
      updateGrade(groupId, null, null, null);
    }
  }, [isLoading, grade, language, type, level, neededText, updateGrade, groupId]);

  const fallbackBoundaryNote =
    resolvedBoundary && resolvedBoundary !== boundary
      ? `Result based on boundary "${resolvedBoundary}" because "${boundary}" is unavailable for this subject.`
      : null;

  if (isLoading) {
    return (
      <div className="calculator-pad">
        <p>Loading…</p>
      </div>
    );
  }
  if (Object.keys(dataByBoundary).length === 0) {
    return (
      <div className="calculator-pad">
        <p>Boundary data unavailable.</p>
      </div>
    );
  }

  const scoreFields = [
    ["p1", "P1", isHL ? 40 : 20, "35%"],
    ["p2", "P2", 30, isHL ? "25%" : "35%"],
    ...(isHL ? [["essay", "Essay", 20, "20%"]] : []),
    ["io", "IO", 40, isHL ? "20%" : "30%"],
  ];


  return (
    <FlowSubjectRow groupId={groupId} fields={scoreFields} scores={scores} onScoresChange={setSc} grade={grade} neededText={neededText} pointsAboveBoundary={pointsAboveBoundary} fallbackBoundaryNote={fallbackBoundaryNote} error={boundaryNotFound ? "Boundary not available. Try another boundary (e.g., m24)." : null} levelControl={<FlowLevelSelect groupId={groupId} value={level} options={LEVELS} onChange={value => { setLvl(value); setLang(pickLanguage(type, value)); }} />} choices={<>
      {headerExtras}{afterTitleExtras}
      <FlowChoice label="Course"><select value={type} onChange={e => { setType(e.target.value); setLang(pickLanguage(e.target.value, level)); setSc({ p1: "", p2: "", essay: "", io: "" }); }}><option value="" disabled>Course</option>{TYPES.map(value => <option key={value} value={value}>{value === "Language and Literature" ? "Language and literature" : value}</option>)}</select></FlowChoice>
      <FlowChoice label="Language" compact><select value={language} disabled={!languages.length} onChange={e => setLang(e.target.value)}><option value="" disabled>Language</option>{languages.map(value => <option key={value} value={value}>{value}</option>)}</select></FlowChoice>
    </>} />
  );
}

export default React.memo(GroupOne);
